<template>
  <el-popover
    width="400"
    trigger="click"
    popper-class="pad-cycle-popover"
    :disabled="!p('Patients.Charts.Event.Export')"
  >
    <el-tabs type="border-card">
      <!-- Leakages Tab -->
      <el-tab-pane :label="$t('text.leakages')">
        <el-checkbox-group v-model="leakageSelection" :max="1">
          <div v-for="leak in dayItem.LeakageFeedbacks" :key="leak.Id">
            <el-checkbox :label="leak.Id">
              {{ leak.FeedbackType }}
            </el-checkbox>
          </div>
        </el-checkbox-group>
        <div class="text-right">
          <el-button
            type="primary"
            size="mini"
            @click="savePadCycleFeedback"
            :loading="savePadCycleFeedbackLoading"
          >
            {{ $t('text.save') }}
          </el-button>
        </div>
      </el-tab-pane>

      <!-- Feedback Tab -->
      <el-tab-pane :label="$t('text.feedback')">
        <el-checkbox-group v-model="dayItem.CheckedFeedbackIds">
          <div v-for="feedback in dayItem.Feedbacks" :key="feedback.Id">
            <el-checkbox :label="feedback.Id">
              {{ feedback.FeedbackType }}
            </el-checkbox>
          </div>
        </el-checkbox-group>
        <div class="text-right">
          <el-button
            type="primary"
            size="mini"
            @click="savePadCycleFeedback"
            :loading="savePadCycleFeedbackLoading"
          >
            {{ $t('text.save') }}
          </el-button>
        </div>
      </el-tab-pane>

      <!-- Pad Weight Tab -->
      <el-tab-pane
        :label="
          $t('text.pad_weight', {
            pad: $firstLetterCapitalize($store.state.locale.pad)
          })
        "
      >
        <!-- Show saved data -->
        <div
          v-if="hasSavedPadWeightData && !isEditing"
          class="text-center pt-20 pb-20"
        >
          <div class="mb-15">
            <i
              class="el-icon-success"
              style="color: #67C23A; font-size: 24px;"
            ></i>
            <div class="font-16 mt-10 mb-10">
              {{
                $t('text.pad_weight_data_saved', {
                  pad: $firstLetterCapitalize($store.state.locale.pad)
                })
              }}
            </div>
            <div class="mb-5">
              <strong
                >{{
                  $t('text.pad_capacity_reached', {
                    pad: $firstLetterCapitalize($store.state.locale.pad)
                  })
                }}:</strong
              >
              {{
                dayItem.PadWeight.IsPadFull
                  ? $firstLetterCapitalize($t('text.yes').toLowerCase())
                  : $firstLetterCapitalize($t('text.no').toLowerCase())
              }}
            </div>
            <div v-if="dayItem.PadWeight.PadWeightValue" class="mb-5">
              <strong>{{ $t('text.actual_weight') }}:</strong>
              {{ dayItem.PadWeight.PadWeightValue }}g
            </div>
            <div v-else class="mb-5">
              <strong>{{ $t('text.actual_weight') }}:</strong>
              {{ $t('text.not_provided') }}
            </div>
          </div>
          <div class="mt-15">
            <el-button type="primary" size="mini" @click="editPadWeight">
              {{ $firstLetterCapitalize($t('text.edit').toLowerCase()) }}
            </el-button>
          </div>
        </div>

        <!-- Question 1: Has the pad reached its capacity? -->
        <div
          v-if="(!hasSavedPadWeightData || isEditing) && !isPadFullAnswered"
          class="text-center pt-20 pb-20"
        >
          <div class="mb-15 font-16">
            {{
              $t('text.pad_weight_first_question', {
                pad: $store.state.locale.pad
              })
            }}
          </div>
          <div class="question-buttons">
            <el-button type="success" size="mini" @click="answerPadFull(true)">
              {{ $firstLetterCapitalize($t('text.yes').toLowerCase()) }}
            </el-button>
            <el-button type="info" size="mini" @click="answerPadFull(false)">
              {{ $firstLetterCapitalize($t('text.no').toLowerCase()) }}
            </el-button>
          </div>
          <div v-if="isEditing" class="mt-10">
            <el-button type="text" size="mini" @click="cancelEdit">
              {{ $t('text.cancel') }}
            </el-button>
          </div>
        </div>

        <!-- Question 2: Do you have actual pad weight? -->
        <div
          v-if="
            (!hasSavedPadWeightData || isEditing) &&
              isPadFullAnswered &&
              !hasActualWeightAnswered
          "
          class="text-center pt-20 pb-20"
        >
          <div class="mb-15 font-16">
            {{ $t('text.pad_weight_second_question') }}
          </div>
          <div class="question-buttons">
            <el-button
              type="success"
              size="mini"
              @click="answerActualWeight(true)"
            >
              {{ $firstLetterCapitalize($t('text.yes').toLowerCase()) }}
            </el-button>
            <el-button
              type="info"
              size="mini"
              @click="answerActualWeight(false)"
            >
              {{ $firstLetterCapitalize($t('text.no').toLowerCase()) }}
            </el-button>
          </div>
          <div class="mt-10">
            <el-button type="text" size="mini" @click="goBackToPadFullQuestion">
              {{ $t('text.back') }}
            </el-button>
          </div>
        </div>

        <!-- Input actual weight -->
        <div
          v-if="
            (!hasSavedPadWeightData || isEditing) &&
              isPadFullAnswered &&
              hasActualWeightAnswered &&
              hasActualWeight
          "
          class="text-center pt-20 pb-20"
        >
          <div class="mb-15">
            <label class="mb-10 mr-5"
              >{{
                $t('text.pad_weight', {
                  pad: $firstLetterCapitalize($store.state.locale.pad)
                })
              }}:</label
            >
            <el-input
              v-model.number="actualPadWeight"
              type="number"
              size="mini"
              style="width: 150px;"
              :min="0"
            >
              <template slot="append"
                >g</template
              >
            </el-input>
          </div>
          <div class="mt-15">
            <el-button
              type="primary"
              size="mini"
              @click="savePadWeightData"
              :loading="savingPadWeight"
              :disabled="!actualPadWeight || actualPadWeight <= 0"
            >
              {{ $t('text.save') }}
            </el-button>
            <el-button type="text" size="mini" @click="goBackToWeightQuestion">
              {{ $t('text.back') }}
            </el-button>
          </div>
        </div>

        <!-- Summary and Save -->
        <div
          v-if="
            (!hasSavedPadWeightData || isEditing) &&
              isPadFullAnswered &&
              hasActualWeightAnswered &&
              !hasActualWeight
          "
          class="text-center pt-20 pb-20"
        >
          <div class="mb-15">
            <div class="font-16 mb-10">
              {{ $firstLetterCapitalize($t('text.summary').toLowerCase()) }}:
            </div>
            <div class="mb-5">
              <strong
                >{{
                  $t('text.pad_capacity_reached', {
                    pad: $firstLetterCapitalize($store.state.locale.pad)
                  })
                }}:</strong
              >
              {{
                isPadFull
                  ? $firstLetterCapitalize($t('text.yes').toLowerCase())
                  : $firstLetterCapitalize($t('text.no').toLowerCase())
              }}
            </div>
            <div class="mb-5">
              <strong>{{ $t('text.actual_weight_provided') }}:</strong>
              {{ $firstLetterCapitalize($t('text.no').toLowerCase()) }}
            </div>
          </div>
          <div class="mt-15">
            <el-button
              type="primary"
              size="mini"
              @click="savePadWeightData"
              :loading="savingPadWeight"
            >
              {{ $t('text.save') }}
            </el-button>
            <el-button type="text" size="mini" @click="goBackToWeightQuestion">
              {{ $t('text.back') }}
            </el-button>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane :label="$t('text.export_data')">
        <div class="text-center pt-20 pb-20">
          <p>
            {{
              $t('text.export_pad_cycle_description', {
                pad: $store.state.locale.pad
              })
            }}
          </p>
          <el-button
            type="primary"
            size="mini"
            icon="el-icon-download"
            @click="exportData"
            :loading="exportLoading"
          >
            {{ $t('text.export_data') }}
          </el-button>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- Popover trigger element -->
    <div
      slot="reference"
      class="pad-cycle-item"
      :class="{
        'pad-start': dayItem.IsStarted === padCycleStartType.isStart,
        'pad-end':
          dayItem.IsCompleted === padCycleEndType.isCompletedImmediate ||
          dayItem.IsCompleted === padCycleEndType.isCompleted,
        'has-feedback': hasAnyFeedback
      }"
    ></div>
  </el-popover>
</template>

<script>
import {
  padCycleEndType,
  padCycleStartType
} from '@/components/patients/charts/smartzEventsChart/SmartzEventConst.js'
import ApiService from '@/services/ApiService.js'
import swal from 'sweetalert'
import moment from 'moment'

export default {
  name: 'PadCycleTooltip',
  props: {
    calculatedData: {
      type: Array,
      required: true
    },
    dayItem: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      padCycleEndType,
      padCycleStartType,
      exportLoading: false,
      savePadCycleFeedbackLoading: false,
      savingPadWeight: false,

      // Pad weight questions
      isPadFullAnswered: false,
      isPadFull: false,
      hasActualWeightAnswered: false,
      hasActualWeight: false,
      actualPadWeight: null,

      // Edit mode
      isEditing: false
    }
  },
  computed: {
    leakageSelection: {
      get() {
        return this.dayItem.CheckedLeakageFeedbackId
          ? [this.dayItem.CheckedLeakageFeedbackId]
          : []
      },
      set(value) {
        this.dayItem.CheckedLeakageFeedbackId =
          Array.isArray(value) && value.length ? value[0] : null
      }
    },
    hasSavedPadWeightData() {
      return (
        this.dayItem.PadWeight &&
        this.dayItem.PadWeight.IsPadFull !== undefined &&
        this.dayItem.PadWeight.IsPadFull !== null
      )
    },
    hasAnyFeedback() {
      const hasFeedbackIds =
        Array.isArray(this.dayItem.CheckedFeedbackIds) &&
        this.dayItem.CheckedFeedbackIds.length > 0
      const hasLeakage = !!this.dayItem.CheckedLeakageFeedbackId
      const hasPadWeight = this.hasSavedPadWeightData
      return hasFeedbackIds || hasLeakage || hasPadWeight
    }
  },
  methods: {
    exportData() {
      let startDate = this.dayItem.StartDate
      let endDate = this.dayItem.EndDate

      if (
        this.dayItem.IsStarted === padCycleStartType.notStart &&
        this.index > 0
      ) {
        let prevDay = this.calculatedData[this.index - 1]
        let prevDayLastPadCycle = prevDay.Data[prevDay.Data.length - 1]
        if (prevDayLastPadCycle) {
          startDate = prevDayLastPadCycle.StartDate
        }
      }

      if (
        this.dayItem.IsCompleted === padCycleEndType.notCompleted &&
        this.index < this.calculatedData.length - 1
      ) {
        let nextDay = this.calculatedData[this.index + 1]
        let nextDayLastPadCycle = nextDay.Data[nextDay.Data.length - 1]
        if (nextDayLastPadCycle) {
          endDate = nextDayLastPadCycle.EndDate
        }
      }

      let podMac = this.dayItem.PodMac
        ? this.dayItem.PodMac
        : this.$store.state.patient.selectedPatient.podMac
      this.exportLoading = true
      ApiService.ExportVoidingPadCycle(
        podMac,
        moment(startDate).format('YYYY-MM-DDTHH:mm:ss'),
        moment(endDate).format('YYYY-MM-DDTHH:mm:ss'),
        this.dayItem.OffSetHour || 0,
        this.dayItem.OffSetHour || 0
      )
        .then(response => {
          this.exportLoading = false
          let FileSaver = require('file-saver')
          let fileName = `Pad Cycle Data.csv`
          let blob = new Blob([response.data], {
            type: 'application/octet-stream'
          })
          FileSaver.saveAs(blob, fileName)
        })
        .catch(() => {
          this.exportLoading = false
          swal({
            icon: 'error',
            title: this.$t('text.failed')
          })
        })
    },

    getFeedbackIds() {
      const ids = Array.isArray(this.dayItem.CheckedFeedbackIds)
        ? this.dayItem.CheckedFeedbackIds.slice()
        : []
      const leakageId = this.dayItem.CheckedLeakageFeedbackId
      if (leakageId) {
        ids.push(leakageId)
      }
      return Array.from(new Set(ids)).filter(
        id => id !== undefined && id !== null && id !== ''
      )
    },

    savePadCycleFeedback() {
      this.savePadCycleFeedbackLoading = true
      const feedbackIds = this.getFeedbackIds() // include pad cycle feedback and leakage feedback
      ApiService.SavePadCycleFeedback({
        PadCycleId: this.dayItem.PadCycleId,
        FeedbackIds: feedbackIds
      })
        .then(response => {
          this.savePadCycleFeedbackLoading = false
          if (response.data && response.data.success) {
            // mark feedback saved status
            this.dayItem.Feedbacks.forEach(feedback => {
              this.$set(
                feedback,
                'Saved',
                (this.dayItem.CheckedFeedbackIds || []).includes(feedback.Id)
              )
            })
            // mark leakage saved status (single choice)
            if (
              this.dayItem.LeakageFeedbacks &&
              this.dayItem.LeakageFeedbacks.length
            ) {
              this.dayItem.LeakageFeedbacks.forEach(leak => {
                this.$set(
                  leak,
                  'Saved',
                  this.dayItem.CheckedLeakageFeedbackId === leak.Id
                )
              })
            }
            swal({
              icon: 'success',
              title: this.$t('text.saved_successfully'),
              timer: 2000
            })
          } else {
            // restore previous saved state
            this.dayItem.CheckedFeedbackIds = this.dayItem.Feedbacks.filter(
              f => f.Saved
            ).map(f => f.Id)
            if (
              this.dayItem.LeakageFeedbacks &&
              this.dayItem.LeakageFeedbacks.length
            ) {
              const savedLeak = this.dayItem.LeakageFeedbacks.find(f => f.Saved)
              this.dayItem.CheckedLeakageFeedbackId = savedLeak
                ? savedLeak.Id
                : null
            }
            swal({
              icon: 'error',
              title: this.$t('text.saved_failed')
            })
          }
        })
        .catch(() => {
          this.savePadCycleFeedbackLoading = false
          this.dayItem.CheckedFeedbackIds = this.dayItem.Feedbacks.filter(
            f => f.Saved
          ).map(f => f.Id)
          if (
            this.dayItem.LeakageFeedbacks &&
            this.dayItem.LeakageFeedbacks.length
          ) {
            const savedLeak = this.dayItem.LeakageFeedbacks.find(f => f.Saved)
            this.dayItem.CheckedLeakageFeedbackId = savedLeak
              ? savedLeak.Id
              : null
          }
          swal({
            icon: 'error',
            title: this.$t('text.failed')
          })
        })
    },

    // Enter edit mode - simply reset and start over
    editPadWeight() {
      this.isEditing = true
      this.resetQuestionStates()
    },

    // Cancel edit mode
    cancelEdit() {
      this.isEditing = false
      this.resetQuestionStates()
    },

    // Answer first question
    answerPadFull(isFull) {
      this.isPadFull = isFull
      this.isPadFullAnswered = true
    },

    // Answer second question
    answerActualWeight(hasWeight) {
      this.hasActualWeight = hasWeight
      this.hasActualWeightAnswered = true
    },

    // Go back to pad full question
    goBackToPadFullQuestion() {
      this.isPadFullAnswered = false
      this.hasActualWeightAnswered = false
      this.hasActualWeight = false
      this.actualPadWeight = null
    },

    // Go back to weight question
    goBackToWeightQuestion() {
      this.hasActualWeightAnswered = false
      this.hasActualWeight = false
      this.actualPadWeight = null
    },

    // Save pad weight data
    async savePadWeightData() {
      if (
        this.hasActualWeight &&
        (!this.actualPadWeight || this.actualPadWeight <= 0)
      ) {
        swal({
          icon: 'warning',
          title: 'Please enter a valid weight'
        })
        return
      }

      this.savingPadWeight = true
      try {
        const response = await ApiService.SavePadWeight({
          PadCycleId: this.dayItem.PadCycleId,
          PadWeightValue: this.hasActualWeight ? this.actualPadWeight : null,
          IsPadFull: this.isPadFull
        })

        if (response.data && response.data.success) {
          // Update dayItem with new data
          this.$set(this.dayItem, 'IsPadFull', this.isPadFull)
          this.$set(this.dayItem, 'PadWeight', {
            IsPadFull: this.isPadFull,
            PadWeightValue: this.hasActualWeight ? this.actualPadWeight : null
          })

          // Exit edit mode and reset
          this.isEditing = false
          this.resetQuestionStates()

          swal({
            icon: 'success',
            title: this.$t('text.saved_successfully'),
            timer: 2000
          })
        } else {
          swal({
            icon: 'error',
            title: this.$t('text.saved_failed')
          })
        }
      } catch (error) {
        swal({
          icon: 'error',
          title: this.$t('text.failed')
        })
      } finally {
        this.savingPadWeight = false
      }
    },

    // Reset question states
    resetQuestionStates() {
      this.isPadFullAnswered = false
      this.isPadFull = false
      this.hasActualWeightAnswered = false
      this.hasActualWeight = false
      this.actualPadWeight = null
    }
  }
}
</script>
